/**
 * Runtime config loader — fetches /assets/config.json at app startup.
 *
 * Used as an APP_INITIALIZER factory.  If the fetch fails (localhost / local
 * dev) the ConfigService keeps its built-in localhost defaults.
 *
 * Mirrors the pattern used by LMS and PRO frontends.
 *
 * ---
 *
 * Chargeur de configuration d'exécution — charge /assets/config.json au
 * démarrage de l'application.  Utilisé comme fabrique APP_INITIALIZER.
 * En cas d'échec (localhost), le ConfigService conserve ses valeurs locales.
 */

import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, of, tap } from 'rxjs';
import { ConfigService, RuntimeConfig } from '../services/config.service';

export function loadRuntimeConfig(): () => Promise<void> {
  // inject() MUST be called here, in the outer factory function, while
  // Angular's injection context is still active.  The inner function runs
  // later (after bootstrapping) when the context is no longer available.
  //
  // inject() DOIT être appelé ici, dans la fonction factory externe, pendant
  // que le contexte d'injection Angular est encore actif.
  const http          = inject(HttpClient);
  const configService = inject(ConfigService);

  return () =>
    new Promise<void>((resolve) => {
      http
        .get<RuntimeConfig>('/assets/config.json')
        .pipe(
          tap((cfg) => configService.setConfig(cfg)),
          catchError((err) => {
            // Expected on localhost — ConfigService will use its localhost defaults.
            // Attendu en local — ConfigService utilisera ses valeurs par défaut.
            console.warn(
              '[Learning] /assets/config.json not found — using localhost defaults.',
              err?.status,
            );
            return of(null);
          }),
        )
        .subscribe({ complete: () => resolve() });
    });
}
